// src/features/kommunikation/components/CredentialsUploadModal.tsx
//
// ZUGANGSDATEN AUS DATEV (05.10.2026): Zeilen von Kunden mit Lohn und Gehalt
// und hr:exchange werden nicht importiert, weil ihre Zugangsdaten automatisch
// aus DATEV entstehen. Das Ergebnis weist sie gesondert aus.

import React, { useState } from 'react';
import { KeyRound, Info, FileUp, CheckCircle2, RefreshCw, SkipForward, XCircle } from 'lucide-react';
import { useAuth } from '../../auth';
import { useCredentials } from '../hooks/useCredentials';

interface CredentialsUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CredentialsUploadModal: React.FC<CredentialsUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { importFromCSV } = useCredentials();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    imported: number;
    updated: number;
    skipped: number;
    errors: string[];
    ausDatev: number;
  } | null>(null);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !user?.email) return;

    setUploading(true);
    setResult(null);

    try {
      const importResult = await importFromCSV(file, user.email);
      
      setResult({
        imported: importResult.imported,
        updated: importResult.updated,
        skipped: importResult.skipped,
        errors: importResult.errors,
        ausDatev: importResult.ausDatev ?? 0
      });

      // Bei Erfolg: Dashboard im Hintergrund aktualisieren,
      // Modal bleibt jedoch offen, damit der User das Result-Panel
      // in Ruhe lesen kann. Schließen erfolgt manuell.
      if (importResult.success) {
        onSuccess();
      }

    } catch (error) {
      console.error('CSV upload error:', error);
      setResult({
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: [error instanceof Error ? error.message : 'Upload fehlgeschlagen'],
        ausDatev: 0
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px',
            backgroundColor: '#05966915', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <KeyRound size={18} color="#059669" />
          </div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
            Credentials importieren (CSV)
          </h2>
        </div>

        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', fontSize: '14px' }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#1E40AF' }}>CSV-Format (Semikolon-getrennt):</div>
          <code style={{ 
            display: 'block', 
            backgroundColor: 'white', 
            padding: '8px', 
            borderRadius: '6px', 
            fontSize: '11px',
            border: '1px solid #E5E7EB',
            overflowX: 'auto',
            whiteSpace: 'nowrap'
          }}>
            Berater;Mandant;...;EMail Mitarbeiter;IBAN der Mitarbeiterbankverbindung;
          </code>
          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Info size={14} color="#6B7280" style={{ flexShrink: 0 }} />
            <span>Als Passwort werden die <strong>letzten 4 Stellen der IBAN</strong> verwendet.</span>
          </div>
          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <Info size={14} color="#6B7280" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>Kunden mit Lohn und Gehalt und DATEV-Datenaustausch werden nicht importiert. Ihre Zugangsdaten kommen automatisch aus DATEV.</span>
          </div>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              padding: '20px',
              border: '2px dashed #D1D5DB',
              borderRadius: '10px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: file ? '#F0FDF4' : '#F9FAFB'
            }}
          >
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
              {file
                ? <CheckCircle2 size={32} color="#059669" />
                : <FileUp size={32} color="#9CA3AF" />}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: file ? '#065F46' : '#374151' }}>
              {file ? file.name : 'CSV-Datei auswählen'}
            </div>
          </label>
        </div>

        {/* Erfolgs-Banner: erscheint nur, wenn tatsächlich etwas neu angelegt oder
            aktualisiert wurde. Bietet einen direkten "Jetzt schließen"-Button.
            Das darunterliegende Detail-Panel bleibt unverändert erhalten. */}
        {result && (result.imported + result.updated) > 0 && (
          <div
            style={{
              marginBottom: '16px',
              padding: '16px',
              borderRadius: '10px',
              backgroundColor: '#ECFDF5',
              border: '1px solid #6EE7B7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <CheckCircle2 size={22} color="#059669" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#065F46' }}>
                  Import erfolgreich
                </div>
                <div style={{ fontSize: '13px', color: '#047857' }}>
                  {result.imported} neu angelegt, {result.updated} aktualisiert
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#059669',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                flexShrink: 0
              }}
            >
              Jetzt schließen
            </button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div
            style={{
              marginBottom: '16px',
              padding: '14px',
              borderRadius: '8px',
              backgroundColor: result.errors.length > 0 ? '#FEF2F2' : '#F0FDF4',
              border: `1px solid ${result.errors.length > 0 ? '#FCA5A5' : '#86EFAC'}`
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '10px', color: '#111827' }}>
              Import-Ergebnis
            </div>
            <div style={{ fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#065F46' }}>
                <CheckCircle2 size={15} color="#059669" /> Neu importiert: <strong>{result.imported}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1E40AF' }}>
                <RefreshCw size={15} color="#3B82F6" /> Aktualisiert: <strong>{result.updated}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280' }}>
                <SkipForward size={15} color="#9CA3AF" /> Übersprungen: <strong>{result.skipped}</strong>
              </div>
              {result.ausDatev > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1E40AF' }}>
                  <Info size={15} color="#3B82F6" /> Nicht importiert, Zugangsdaten kommen aus DATEV: <strong>{result.ausDatev}</strong>
                </div>
              )}
              {result.errors.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontWeight: '600', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <XCircle size={15} color="#DC2626" /> Fehler:
                  </div>
                  <ul style={{ margin: '4px 0', paddingLeft: '24px', fontSize: '12px', color: '#991B1B' }}>
                    {result.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {result.errors.length > 5 && (
                      <li>... und {result.errors.length - 5} weitere Fehler</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            {result ? 'Schließen' : 'Abbrechen'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: !file || uploading ? '#D1D5DB' : '#3B82F6',
              color: 'white',
              cursor: !file || uploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {uploading ? 'Importiere...' : 'Importieren'}
          </button>
        </div>
      </div>
    </div>
  );
};
